import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../../../../lib/utils';
import { colLabel, displayLabel, isDefaultEnglishLabel } from './constants';
import { isDateFaFormula } from '../../utils/reportFormat';
import {
  BAR_MARKER_OPTIONS,
  BAR_SIZE_OPTIONS,
  DISPLAY_TYPES,
  EMOJI_PRESET_OPTIONS,
  HEADER_ALIGN_OPTIONS,
  TEXT_SIZE_OPTIONS,
  isMeterDisplay,
  compileMathToSql,
  compileTemplateToSql,
  displayToMath,
  filterColumnSuggestions,
  insertColumnToken,
  isCustomColumn,
  mathToDisplay,
  parseColumnWidth,
  resolveTableLook,
  sqlIdent,
  trailingColumnQuery,
} from '../../utils/tableColumnDisplay';

const inputCls = 'w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-300';
const selectCls = inputCls;

const FORMULA_PRESETS = [
  { value: '', label: 'بدون تبدیل' },
  { value: 'round:0', label: 'ROUND (۰)' },
  { value: 'round:2', label: 'ROUND (۲)' },
  { value: 'abs', label: 'ABS' },
  { value: 'percent', label: '×۱۰۰' },
  { value: 'date:fa', label: 'تاریخ شمسی' },
  { value: '__custom__', label: 'فرمول JS…' },
];

const FORMAT_OPTIONS = [
  { value: 'number', label: 'عدد' },
  { value: 'percent', label: 'درصد' },
  { value: 'currency', label: 'مبلغ' },
  { value: 'text', label: 'متن' },
];
const CURRENCY_SUFFIX_OPTIONS = [
  { value: 'rial', label: 'ریال' },
  { value: 'sign', label: '﷼' },
  { value: 'none', label: 'بدون واحد' },
];
const ALIGN_OPTIONS = [
  { value: 'right', label: 'راست' },
  { value: 'center', label: 'وسط' },
  { value: 'left', label: 'چپ' },
];
const CONDITIONAL_OPS = ['>=', '<=', '>', '<', '='];
const DIR_OPTIONS = [
  { value: '', label: 'خودکار' },
  { value: 'rtl', label: 'RTL' },
  { value: 'ltr', label: 'LTR' },
];
const MATH_OPS = [
  { tok: ' + ', label: '+' },
  { tok: ' - ', label: '−' },
  { tok: ' * ', label: '×' },
  { tok: ' / ', label: '÷' },
  { tok: '(', label: '(' },
  { tok: ')', label: ')' },
];

function ChoiceRow({ label, value, options, onChange }) {
  return (
    <div className="space-y-1">
      {label && <p className="text-[10px] font-medium text-slate-500">{label}</p>}
      <div className="flex rounded-lg border border-slate-200 overflow-hidden bg-white">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            title={o.hint || o.label}
            onClick={() => onChange(o.value)}
            className={cn(
              'flex-1 py-1 px-0.5 text-[10px] font-bold',
              value === o.value ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-50',
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ColorSwatch({ label, value, onChange }) {
  const empty = !value;
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[10px] font-medium text-slate-500">{label}</span>
      <div className="flex items-center gap-1">
        <input
          type="color"
          disabled={empty}
          value={value || '#334155'}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 w-7 rounded border border-slate-200 cursor-pointer disabled:opacity-40 shrink-0"
        />
        <button
          type="button"
          onClick={() => onChange(empty ? '#334155' : '')}
          className={cn(
            'flex-1 min-w-0 rounded-md border px-1 py-1 text-[9px] truncate',
            empty ? 'border-dashed border-slate-300 text-slate-400' : 'border-slate-200 text-slate-600',
          )}
        >
          {empty ? 'پیش‌فرض' : value}
        </button>
      </div>
    </div>
  );
}

function SuggestionList({ items, highlight, onPick, onHover }) {
  if (!items.length) {
    return (
      <div className="px-2 py-2 text-[11px] text-slate-400">ستونی مطابق جستجو نیست</div>
    );
  }
  return (
    <ul role="listbox" className="max-h-80 overflow-y-auto py-0.5">
      {items.map((key, i) => (
        <li key={key} role="option" aria-selected={i === highlight}>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(key);
            }}
            onMouseEnter={() => onHover(i)}
            className={cn(
              'flex w-full items-start px-2.5 py-2 text-right',
              i === highlight ? 'bg-violet-100 text-violet-900' : 'hover:bg-slate-50',
            )}
          >
            <span className="w-full whitespace-normal break-words text-[12px] font-medium leading-snug text-slate-800">
              {colLabel(key)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Typeahead that inserts `{key}` and never removes a picked column from suggestions. */
function ColumnSuggestInput({
  value,
  onChange,
  fieldOptions,
  placeholder,
  className,
  children,
  multiline = false,
  dir = 'rtl',
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [cursor, setCursor] = useState(null);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  const pos = cursor == null ? String(value || '').length : cursor;
  const trail = trailingColumnQuery(value, pos);
  const suggestions = filterColumnSuggestions(fieldOptions, trail.query, colLabel);

  useEffect(() => {
    if (highlight >= suggestions.length) setHighlight(0);
  }, [suggestions.length, highlight]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const syncCursor = () => {
    const el = inputRef.current;
    if (el) setCursor(el.selectionStart);
  };

  const placeCaret = (nextCursor) => {
    setCursor(nextCursor);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const livePos = () => {
    const el = inputRef.current;
    return el && el.selectionStart != null ? el.selectionStart : pos;
  };

  const pick = (key) => {
    const next = insertColumnToken(value, key, livePos(), colLabel(key));
    onChange(next.text);
    setHighlight(0);
    setOpen(true);
    placeCaret(next.cursor);
  };

  const insertChunk = (chunk) => {
    const start = livePos();
    const el = inputRef.current;
    const end = el && el.selectionEnd != null ? el.selectionEnd : start;
    const next = `${(value || '').slice(0, start)}${chunk}${(value || '').slice(end)}`;
    onChange(next);
    setOpen(true);
    placeCaret(start + String(chunk).length);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (suggestions.length ? (h + 1) % suggestions.length : 0));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (suggestions.length ? (h - 1 + suggestions.length) % suggestions.length : 0));
      return;
    }
    if (e.key === 'Enter' && open && suggestions[highlight]) {
      e.preventDefault();
      pick(suggestions[highlight]);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="flex items-start gap-1">
        {multiline ? (
          <textarea
            ref={inputRef}
            className={cn(className, 'flex-1 min-w-0 min-h-[4.5rem] resize-y leading-relaxed')}
            dir={dir}
            rows={3}
            placeholder={placeholder}
            value={value || ''}
            onChange={(e) => {
              onChange(e.target.value);
              setCursor(e.target.selectionStart);
              setOpen(true);
              setHighlight(0);
            }}
            onFocus={() => {
              syncCursor();
              setOpen(true);
            }}
            onClick={syncCursor}
            onKeyUp={syncCursor}
            onKeyDown={onKeyDown}
          />
        ) : (
          <input
            ref={inputRef}
            className={cn(className, 'flex-1 min-w-0')}
            dir={dir}
            placeholder={placeholder}
            value={value || ''}
            onChange={(e) => {
              onChange(e.target.value);
              setCursor(e.target.selectionStart);
              setOpen(true);
              setHighlight(0);
            }}
            onFocus={() => {
              syncCursor();
              setOpen(true);
            }}
            onClick={syncCursor}
            onKeyUp={syncCursor}
            onKeyDown={onKeyDown}
          />
        )}
        <button
          type="button"
          title="پیشنهاد ستون"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setOpen((v) => !v);
            inputRef.current?.focus();
          }}
          className="shrink-0 rounded-md border border-slate-200 bg-white px-1.5 py-1 text-[10px] text-slate-500 hover:bg-slate-50"
        >
          ▼
        </button>
      </div>
      {typeof children === 'function' ? children({ insert: insertChunk }) : children}
      {open && (
        <div className="absolute z-30 top-full mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg">
          <SuggestionList
            items={suggestions}
            highlight={highlight}
            onPick={pick}
            onHover={setHighlight}
          />
        </div>
      )}
    </div>
  );
}

/** Searchable single-column picker. Options stay in the list after a pick. */
function ColumnKeyPicker({ value, fieldOptions, onChange, placeholder = '— ستون داده —' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const suggestions = filterColumnSuggestions(fieldOptions, query, colLabel);

  useEffect(() => {
    if (highlight >= suggestions.length) setHighlight(0);
  }, [suggestions.length, highlight]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current?.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (key) => {
    onChange(key);
    setQuery('');
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          className={cn(inputCls, 'flex-1 min-w-0')}
          dir="rtl"
          placeholder={value ? colLabel(value) : placeholder}
          value={open ? query : (value ? colLabel(value) : '')}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => {
            setQuery('');
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
              setHighlight((h) => (suggestions.length ? (h + 1) % suggestions.length : 0));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => (suggestions.length ? (h - 1 + suggestions.length) % suggestions.length : 0));
            } else if (e.key === 'Enter' && open && suggestions[highlight]) {
              e.preventDefault();
              pick(suggestions[highlight]);
            } else if (e.key === 'Escape') {
              setOpen(false);
              setQuery('');
            }
          }}
        />
        <button
          type="button"
          title="پیشنهاد ستون"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setOpen((v) => !v);
            setQuery('');
            inputRef.current?.focus();
          }}
          className="shrink-0 rounded-md border border-slate-200 bg-white px-1.5 py-1 text-[10px] text-slate-500 hover:bg-slate-50"
        >
          ▼
        </button>
      </div>
      {open && (
        <div className="absolute z-30 top-full mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg">
          <SuggestionList
            items={suggestions}
            highlight={highlight}
            onPick={pick}
            onHover={setHighlight}
          />
        </div>
      )}
    </div>
  );
}

export function TableLookFields({ tableConfig = {}, onChange }) {
  const look = resolveTableLook(tableConfig);
  return (
    <div className="space-y-2.5 rounded-xl border border-slate-200 bg-slate-50/70 p-2.5">
      <p className="text-[11px] font-bold text-slate-600">ظاهر جدول</p>
      <ChoiceRow
        label="اندازه متن سلول"
        value={look.textSize}
        options={TEXT_SIZE_OPTIONS}
        onChange={(textSize) => onChange({ textSize })}
      />
      <ChoiceRow
        label="اندازه عنوان ستون"
        value={look.headerTextSize}
        options={TEXT_SIZE_OPTIONS}
        onChange={(headerTextSize) => onChange({ headerTextSize })}
      />
      <ChoiceRow
        label="تراز عنوان ستون"
        value={look.headerAlign}
        options={HEADER_ALIGN_OPTIONS}
        onChange={(headerAlign) => onChange({ headerAlign })}
      />
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => onChange({ headerBold: !look.headerBold })}
          className={cn(
            'rounded-md border px-2 py-1 text-[10px] font-bold',
            look.headerBold ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-200 bg-white text-slate-500',
          )}
        >
          عنوان ضخیم
        </button>
        <button
          type="button"
          onClick={() => onChange({ headerItalic: !look.headerItalic })}
          className={cn(
            'rounded-md border px-2 py-1 text-[10px] font-bold italic',
            look.headerItalic ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-200 bg-white text-slate-500',
          )}
        >
          مورب
        </button>
        <button
          type="button"
          onClick={() => onChange({ compact: !look.compact })}
          className={cn(
            'rounded-md border px-2 py-1 text-[10px] font-bold',
            look.compact ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-200 bg-white text-slate-500',
          )}
        >
          ردیف فشرده
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <ColorSwatch label="رنگ عنوان" value={look.headerColor} onChange={(headerColor) => onChange({ headerColor })} />
        <ColorSwatch label="پس‌زمینه عنوان" value={look.headerBg} onChange={(headerBg) => onChange({ headerBg })} />
      </div>
    </div>
  );
}

function ColumnCard({ col, index, total, fieldOptions, open, onToggle, onUpdate, onRemove, onMove }) {
  const custom = isCustomColumn(col);
  const compute = col.compute || (col.math ? 'math' : (col.template ? 'template' : (custom ? 'math' : 'field')));
  const formulaVal = col.formula || '';
  const dateFa = isDateFaFormula(formulaVal);
  const matchedPreset = FORMULA_PRESETS.some((p) => p.value && p.value === formulaVal);
  const isCustomFormula = formulaVal && !matchedPreset && !dateFa;
  const formulaSelect = isCustomFormula
    ? '__custom__'
    : (matchedPreset ? formulaVal : (dateFa ? 'date:fa' : formulaVal));
  const datePattern = formulaVal === 'date:fa' || !dateFa
    ? 'yyyy/MM/dd'
    : formulaVal.slice('date:fa:'.length);
  const display = col.display || 'text';
  const rules = col.conditionalFormat || [];
  const title = displayLabel(col) || (custom ? 'ستون سفارشی' : 'ستون جدید');
  const sized = parseColumnWidth(col);
  const mathSql = (custom && compute === 'math')
    ? compileMathToSql(col.math, (k) => k, fieldOptions)
    : '';
  const templateSql = (custom && compute === 'template')
    ? compileTemplateToSql(col.template, (k) => k)
    : '';

  return (
    <div className={cn(
      'rounded-xl border bg-white transition-shadow',
      open ? 'border-indigo-200 shadow-sm' : 'border-slate-200',
    )}>
      <div className="flex items-center gap-1 px-2 py-1.5 bg-slate-50/80 rounded-t-xl">
        <div className="flex flex-col shrink-0">
          <button type="button" disabled={index === 0} onClick={() => onMove(-1)} className="px-1 text-[9px] text-slate-400 hover:text-indigo-600 disabled:opacity-30 leading-none">▲</button>
          <button type="button" disabled={index >= total - 1} onClick={() => onMove(1)} className="px-1 text-[9px] text-slate-400 hover:text-indigo-600 disabled:opacity-30 leading-none">▼</button>
        </div>
        <button type="button" onClick={onToggle} className="flex-1 min-w-0 text-right">
          <div className="flex items-center gap-1.5">
            <span className={cn(
              'shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold',
              custom ? 'bg-violet-100 text-violet-800' : 'bg-slate-200 text-slate-600',
            )}>
              {custom ? 'سفارشی' : 'داده'}
            </span>
            <span className="truncate text-[12px] font-bold text-slate-800">{title}</span>
          </div>
          <div className="text-[9px] text-slate-400 whitespace-normal break-words leading-snug mt-0.5" dir="rtl">
            {custom
              ? (col.math
                ? mathToDisplay(col.math, fieldOptions)
                : (col.template ? mathToDisplay(col.template, fieldOptions) : colLabel(col.key)))
              : colLabel(col.key)}
          </div>
        </button>
        {sized && (
          <span className="shrink-0 rounded bg-slate-100 px-1 py-0.5 text-[9px] font-mono text-slate-500" dir="ltr">
            {sized.value}{sized.unit}
          </span>
        )}
        <span className="shrink-0 text-[9px] text-slate-400">{DISPLAY_TYPES.find((d) => d.value === display)?.label}</span>
        <button type="button" onClick={onRemove} className="shrink-0 text-slate-400 hover:text-red-500 px-1" title="حذف">✕</button>
      </div>

      {open && (
        <div className="px-2.5 py-2 space-y-2 border-t border-slate-100">
          <input
            className={inputCls}
            placeholder="عنوان فارسی ستون (alias)"
            value={col.label || ''}
            onChange={(e) => onUpdate({ label: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] font-medium text-slate-500">عرض ستون</span>
              <input
                type="number"
                min={0}
                step="any"
                className={inputCls}
                dir="ltr"
                placeholder="خودکار"
                value={col.width === 0 || col.width ? col.width : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  onUpdate({ width: v === '' ? '' : Number(v) });
                }}
              />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] font-medium text-slate-500">واحد</span>
              <select
                className={selectCls}
                value={col.widthUnit === 'px' ? 'px' : '%'}
                onChange={(e) => onUpdate({ widthUnit: e.target.value })}
              >
                <option value="%">درصد</option>
                <option value="px">پیکسل</option>
              </select>
            </label>
          </div>
          {custom && (
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] font-medium text-slate-500">نام انگلیسی ستون (SQL)</span>
              <input
                className={`${inputCls} font-mono`}
                dir="ltr"
                placeholder="net_plus_return"
                value={col.key || ''}
                onChange={(e) => onUpdate({ key: sqlIdent(e.target.value) || col.key })}
              />
            </label>
          )}

          {!custom && (
            <ColumnKeyPicker
              value={col.key}
              fieldOptions={fieldOptions}
              onChange={(key) => {
                const keepCustom = col.label
                  && col.label !== col.key
                  && col.label !== colLabel(col.key)
                  && !isDefaultEnglishLabel(col.label, col.key);
                onUpdate({
                  key,
                  source: 'field',
                  label: keepCustom ? col.label : colLabel(key),
                });
              }}
            />
          )}

          {custom && (
            <div className="flex rounded-lg border border-slate-200 overflow-hidden">
              {[
                { id: 'math', label: 'محاسبه' },
                { id: 'template', label: 'ترکیب متن' },
              ].map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => onUpdate({ compute: m.id, format: m.id === 'math' ? (col.format || 'number') : 'text' })}
                  className={cn(
                    'flex-1 py-1 text-[11px] font-bold',
                    compute === m.id ? 'bg-violet-600 text-white' : 'bg-white text-slate-500 hover:bg-violet-50',
                  )}
                >
                  {m.label}
                </button>
              ))}
            </div>
          )}

          {(custom && compute === 'math') && (
            <div className="space-y-1.5 rounded-xl border border-violet-200 bg-gradient-to-b from-violet-50/80 to-white p-2.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-bold text-violet-800">فرمول محاسبه</p>
                <span className="text-[9px] text-violet-500">نمایش فارسی · کلید انگلیسی در SQL</span>
              </div>
              <ColumnSuggestInput
                multiline
                dir="rtl"
                className={`${inputCls} text-[13px]`}
                placeholder={'{کارتن مرجوعی حواله با علت} + {کارتن جایزه مرجوعی حواله با علت} + {کارتن برگشتی و جایزه برگشتی با علت}'}
                value={mathToDisplay(col.math, fieldOptions)}
                fieldOptions={fieldOptions}
                onChange={(display) => onUpdate({ math: displayToMath(display, fieldOptions), compute: 'math' })}
              >
                {({ insert }) => (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {MATH_OPS.map((op) => (
                      <button
                        key={op.label}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insert(op.tok)}
                        className="min-w-[32px] h-7 rounded-lg border border-violet-200 bg-white px-2 text-[13px] font-bold text-violet-800 shadow-sm hover:bg-violet-50"
                      >
                        {op.label}
                      </button>
                    ))}
                  </div>
                )}
              </ColumnSuggestInput>
              {!!mathSql && (
                <p className="rounded-md bg-slate-900 px-2 py-1 font-mono text-[10px] text-emerald-300 leading-relaxed" dir="ltr">
                  {`SELECT ${mathSql} AS ${sqlIdent(col.key) || 'x'}`}
                </p>
              )}
              <p className="text-[9px] text-slate-500 leading-relaxed">
                ستون‌ها را با تایپ نام فارسی یا انگلیسی انتخاب کنید؛ تکرار مجاز است و از لیست حذف نمی‌شوند.
              </p>
            </div>
          )}

          {(custom ? compute === 'template' : !!col.template) && (
            <div className="space-y-1.5 rounded-xl border border-slate-200 bg-slate-50/60 p-2.5">
              <label className="text-[11px] font-bold text-slate-600">ترکیب متن</label>
              <ColumnSuggestInput
                multiline
                dir="rtl"
                className={`${inputCls} text-[13px]`}
                placeholder={'{نام پرسنل} — {شغل}'}
                value={mathToDisplay(col.template, fieldOptions)}
                fieldOptions={fieldOptions}
                onChange={(display) => onUpdate({
                  template: displayToMath(display, fieldOptions),
                  compute: custom ? 'template' : col.compute,
                })}
              />
              {!!templateSql && (
                <p className="rounded-md bg-slate-900 px-2 py-1 font-mono text-[10px] text-emerald-300 leading-relaxed" dir="ltr">
                  {`SELECT ${templateSql} AS ${sqlIdent(col.key) || 'x'}`}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-1">
            {DISPLAY_TYPES.map((d) => (
              <button
                key={d.value}
                type="button"
                onClick={() => onUpdate({ display: d.value })}
                className={cn(
                  'rounded-full border px-2 py-0.5 text-[10px] font-bold',
                  display === d.value
                    ? 'border-indigo-400 bg-indigo-50 text-indigo-800'
                    : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50',
                )}
              >
                {d.label}
              </button>
            ))}
          </div>

          {isMeterDisplay(display) && (
            <div className="space-y-2 rounded-xl border border-indigo-200 bg-indigo-50/50 p-2.5">
              <p className="text-[11px] font-bold text-indigo-800">
                {DISPLAY_TYPES.find((d) => d.value === display)?.label || 'نوار'} · فروش / هدف
              </p>
              <ChoiceRow
                label="اندازه"
                value={col.barSize || col.textSize || 'sm'}
                options={BAR_SIZE_OPTIONS}
                onChange={(barSize) => onUpdate({ barSize })}
              />
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-[10px] font-medium text-slate-600">شروع (فروش)</span>
                  <select
                    className={selectCls}
                    value={col.barStartKey || col.key || ''}
                    onChange={(e) => onUpdate({ barStartKey: e.target.value })}
                  >
                    <option value="">این ستون</option>
                    {fieldOptions.map((key) => (
                      <option key={key} value={key}>{colLabel(key)}</option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-[10px] font-medium text-slate-600">پایان (هدف)</span>
                  <select
                    className={selectCls}
                    value={col.barEndKey || ''}
                    onChange={(e) => onUpdate({ barEndKey: e.target.value })}
                  >
                    <option value="">سقف ثابت</option>
                    {fieldOptions.map((key) => (
                      <option key={key} value={key}>{colLabel(key)}</option>
                    ))}
                  </select>
                </label>
              </div>
              {!col.barEndKey && (
                <label className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-medium text-slate-500">سقف ثابت</span>
                  <input
                    type="number"
                    min={1}
                    className={inputCls}
                    placeholder="بدون سقف = بدون پیشرفت"
                    value={col.barMax === 0 || col.barMax ? col.barMax : ''}
                    onChange={(e) => onUpdate({ barMax: e.target.value === '' ? '' : Number(e.target.value) })}
                  />
                </label>
              )}
              <div className="grid grid-cols-2 gap-2">
                <ColorSwatch label="رنگ پیش‌فرض" value={col.barColor || ''} onChange={(barColor) => onUpdate({ barColor })} />
                <ColorSwatch label="رنگ زمینه" value={col.barTrackColor || ''} onChange={(barTrackColor) => onUpdate({ barTrackColor })} />
              </div>
              <ChoiceRow
                label="نشانگر موقعیت"
                value={col.barMarker || 'none'}
                options={BAR_MARKER_OPTIONS}
                onChange={(barMarker) => onUpdate({ barMarker })}
              />
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <label className="flex items-center gap-1.5 text-[10px] font-bold text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-indigo-600"
                    checked={!!col.barBorder}
                    onChange={(e) => onUpdate({ barBorder: e.target.checked })}
                  />
                  حاشیه
                </label>
                {!!col.barBorder && (
                  <ColorSwatch
                    label="رنگ حاشیه"
                    value={col.barBorderColor || ''}
                    onChange={(barBorderColor) => onUpdate({ barBorderColor })}
                  />
                )}
                <label className="flex items-center gap-1.5 text-[10px] font-bold text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-indigo-600"
                    checked={col.barShowLabels !== false}
                    onChange={(e) => onUpdate({ barShowLabels: e.target.checked })}
                  />
                  عدد فروش / هدف
                </label>
                <label className="flex items-center gap-1.5 text-[10px] font-bold text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-indigo-600"
                    checked={col.barShowPercent !== false}
                    onChange={(e) => onUpdate({ barShowPercent: e.target.checked })}
                  />
                  درصد وسط
                </label>
                <label className="flex items-center gap-1.5 text-[10px] font-bold text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-indigo-600"
                    checked={!!col.barFillRow}
                    onChange={(e) => onUpdate({ barFillRow: e.target.checked })}
                  />
                  پر کردن ردیف
                </label>
              </div>
              <p className="text-[9px] text-slate-500 leading-relaxed">
                قانون شرطی روی درصد تحقق (۰ تا ۱۰۰) اعمال می‌شود، نه مبلغ فروش.
              </p>
            </div>
          )}

          {display === 'stars' && (
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] font-medium text-slate-500">سقف برای ۵ ستاره</span>
              <input
                type="number"
                min={1}
                className={inputCls}
                value={col.barMax != null ? col.barMax : 100}
                onChange={(e) => onUpdate({ barMax: Number(e.target.value) || 100 })}
              />
            </label>
          )}

          <div className="flex rounded-lg border border-slate-200 overflow-hidden bg-white">
            {DIR_OPTIONS.map((d) => (
              <button
                key={d.value || 'auto'}
                type="button"
                onClick={() => onUpdate({ dir: d.value })}
                className={cn(
                  'flex-1 py-1 text-[10px] font-bold',
                  (col.dir || '') === d.value
                    ? 'bg-slate-800 text-white'
                    : 'text-slate-500 hover:bg-slate-50',
                )}
              >
                {d.label}
              </button>
            ))}
          </div>

          {display === 'emoji' && (
            <div className="space-y-1">
              <select
                className={selectCls}
                value={col.emojiPreset || 'traffic'}
                onChange={(e) => onUpdate({
                  emojiPreset: e.target.value,
                  emojiRules: e.target.value === 'custom'
                    ? (col.emojiRules?.length ? col.emojiRules : [{ min: 80, emoji: '🟢' }, { min: 0, emoji: '🔴' }])
                    : col.emojiRules,
                })}
              >
                {EMOJI_PRESET_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              {col.emojiPreset === 'custom' && (col.emojiRules || []).map((r, ri) => (
                <div key={ri} className="flex items-center gap-1">
                  <span className="text-[9px] text-slate-400">≥</span>
                  <input
                    type="number"
                    className="w-16 rounded border border-slate-200 px-1 py-0.5 text-[10px]"
                    value={r.min}
                    onChange={(e) => {
                      const next = (col.emojiRules || []).map((x, i) => (i === ri ? { ...x, min: Number(e.target.value) } : x));
                      onUpdate({ emojiRules: next });
                    }}
                  />
                  <input
                    className="flex-1 rounded border border-slate-200 px-1 py-0.5 text-sm text-center"
                    value={r.emoji || ''}
                    onChange={(e) => {
                      const next = (col.emojiRules || []).map((x, i) => (i === ri ? { ...x, emoji: e.target.value } : x));
                      onUpdate({ emojiRules: next });
                    }}
                  />
                  <button
                    type="button"
                    className="text-slate-400 hover:text-red-500"
                    onClick={() => onUpdate({ emojiRules: (col.emojiRules || []).filter((_, i) => i !== ri) })}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-2.5">
            <p className="text-[11px] font-bold text-slate-600">ظاهر سلول</p>
            <ChoiceRow
              label="اندازه متن"
              value={col.textSize || ''}
              options={[{ value: '', label: 'جدول', hint: 'از تنظیم جدول' }, ...TEXT_SIZE_OPTIONS]}
              onChange={(textSize) => onUpdate({ textSize })}
            />
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] font-medium text-slate-500">فرمت</span>
                <select
                  className={selectCls}
                  value={col.format || 'number'}
                  onChange={(e) => {
                    const format = e.target.value;
                    const patch = { format };
                    if (format !== 'text' && (col.decimals == null || col.decimals === '')) {
                      patch.decimals = 2;
                    }
                    if (format === 'currency' && (!col.currencySuffix || col.currencySuffix === 'none')) {
                      patch.currencySuffix = 'rial';
                    }
                    if (format === 'number' || format === 'currency') {
                      if (col.useGrouping == null) patch.useGrouping = true;
                    }
                    if (format === 'percent') patch.useGrouping = false;
                    onUpdate(patch);
                  }}
                >
                  {FORMAT_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] font-medium text-slate-500">تراز سلول</span>
                <select className={selectCls} value={col.align || 'right'} onChange={(e) => onUpdate({ align: e.target.value })}>
                  {ALIGN_OPTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </label>
            </div>
            {col.format !== 'text' && (
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-medium text-slate-500">اعشار (پیش‌فرض ۲)</span>
                  <input
                    type="number"
                    min={0}
                    max={6}
                    step={1}
                    dir="ltr"
                    className={`${selectCls} text-left`}
                    value={col.decimals == null || col.decimals === '' ? 2 : col.decimals}
                    onChange={(e) => onUpdate({ decimals: e.target.value === '' ? 2 : Number(e.target.value) })}
                  />
                </label>
                <label className="flex items-end gap-2 pb-1 text-[11px] text-slate-600">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-slate-300"
                    checked={col.useGrouping !== false}
                    onChange={(e) => onUpdate({ useGrouping: e.target.checked })}
                  />
                  جداکننده هزارگان
                </label>
              </div>
            )}
            {col.format === 'currency' && (
              <ChoiceRow
                label="نمایش واحد"
                value={col.currencySuffix || 'rial'}
                options={CURRENCY_SUFFIX_OPTIONS}
                onChange={(currencySuffix) => onUpdate({ currencySuffix })}
              />
            )}
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => onUpdate({ bold: !col.bold })}
                className={cn(
                  'rounded-md border px-2 py-1 text-[10px] font-bold',
                  col.bold ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-200 text-slate-500',
                )}
              >
                ضخیم
              </button>
              <button
                type="button"
                onClick={() => onUpdate({ italic: !col.italic })}
                className={cn(
                  'rounded-md border px-2 py-1 text-[10px] italic font-bold',
                  col.italic ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-200 text-slate-500',
                )}
              >
                مورب
              </button>
            </div>
          </div>

          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] font-medium text-slate-500">تبدیل</span>
            <select
              className={selectCls}
              value={formulaSelect}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '__custom__') {
                  onUpdate({ formula: col.formula && isCustomFormula ? col.formula : 'Number(value)' });
                } else {
                  onUpdate({ formula: v });
                }
              }}
            >
              {FORMULA_PRESETS.map((p) => <option key={p.value || 'none'} value={p.value}>{p.label}</option>)}
            </select>
          </label>
          {(formulaSelect === '__custom__' || isCustomFormula) && (
            <input
              className={`${inputCls} font-mono`}
              dir="ltr"
              placeholder="Number(value)"
              value={formulaVal}
              onChange={(e) => onUpdate({ formula: e.target.value })}
            />
          )}
          {dateFa && !isCustomFormula && (
            <input
              className={`${inputCls} font-mono`}
              dir="ltr"
              placeholder="yyyy-MM-dd HH:mm"
              value={datePattern}
              onChange={(e) => {
                const p = e.target.value.trim();
                onUpdate({ formula: !p || p === 'yyyy/MM/dd' ? 'date:fa' : `date:fa:${p}` });
              }}
            />
          )}

          <div className="grid grid-cols-2 gap-2">
            <ColorSwatch label="رنگ متن" value={col.fontColor || ''} onChange={(v) => onUpdate({ fontColor: v })} />
            <ColorSwatch label="پس‌زمینه" value={col.bgColor || ''} onChange={(v) => onUpdate({ bgColor: v })} />
          </div>

          <div className="space-y-1">
            <p className="text-[10px] font-bold text-slate-500">قالب‌بندی شرطی</p>
            {rules.map((rule, ri) => (
              <div key={ri} className="rounded-lg border border-slate-200 bg-slate-50 p-1.5 space-y-1">
                <div className="flex items-center gap-1">
                  <select
                    className="rounded border border-slate-200 px-1 py-0.5 text-[10px]"
                    value={rule.op}
                    onChange={(e) => {
                      const next = rules.map((r, i) => (i === ri ? { ...r, op: e.target.value } : r));
                      onUpdate({ conditionalFormat: next });
                    }}
                  >
                    {CONDITIONAL_OPS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <input
                    type="number"
                    className="w-16 rounded border border-slate-200 px-1 py-0.5 text-[10px]"
                    value={rule.value}
                    onChange={(e) => {
                      const next = rules.map((r, i) => (i === ri ? { ...r, value: Number(e.target.value) } : r));
                      onUpdate({ conditionalFormat: next });
                    }}
                  />
                  <select
                    className="flex-1 rounded border border-slate-200 px-1 py-0.5 text-[10px]"
                    value={rule.apply || 'cell'}
                    onChange={(e) => {
                      const next = rules.map((r, i) => (i === ri ? { ...r, apply: e.target.value } : r));
                      onUpdate({ conditionalFormat: next });
                    }}
                  >
                    <option value="cell">سلول</option>
                    <option value="bar">نوار / گیج</option>
                    <option value="row">کل ردیف</option>
                  </select>
                  <button
                    type="button"
                    className="text-slate-400 hover:text-red-500"
                    onClick={() => onUpdate({ conditionalFormat: rules.filter((_, i) => i !== ri) })}
                  >
                    ✕
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1 text-[9px] text-slate-500">
                    متن
                    <input
                      type="color"
                      value={rule.color || '#166534'}
                      onChange={(e) => {
                        const next = rules.map((r, i) => (i === ri ? { ...r, color: e.target.value } : r));
                        onUpdate({ conditionalFormat: next });
                      }}
                      className="h-6 w-6 rounded border border-slate-200 cursor-pointer"
                    />
                  </label>
                  <label className="flex items-center gap-1 text-[9px] text-slate-500">
                    پس‌زمینه
                    <input
                      type="color"
                      value={rule.bg || '#dcfce7'}
                      onChange={(e) => {
                        const next = rules.map((r, i) => (i === ri ? { ...r, bg: e.target.value } : r));
                        onUpdate({ conditionalFormat: next });
                      }}
                      className="h-6 w-6 rounded border border-slate-200 cursor-pointer"
                    />
                  </label>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => onUpdate({
                conditionalFormat: [...rules, {
                  op: '>=', value: 80, color: '#166534', bg: '#dcfce7', apply: isMeterDisplay(display) ? 'bar' : 'cell',
                }],
              })}
              className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800"
            >
              + قانون رنگ
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TableColumnsEditor({
  columns = [],
  fieldOptions = [],
  onUpdate,
  onRemove,
  onMove,
  onAddField,
  onAddCustom,
}) {
  const [openIdx, setOpenIdx] = useState(columns.length ? 0 : -1);

  return (
    <div className="space-y-2">
      {columns.map((col, i) => (
        <ColumnCard
          key={col.key || `col-${i}`}
          col={col}
          index={i}
          total={columns.length}
          fieldOptions={fieldOptions}
          open={openIdx === i}
          onToggle={() => setOpenIdx((cur) => (cur === i ? -1 : i))}
          onUpdate={(patch) => onUpdate(i, patch)}
          onRemove={() => onRemove(i)}
          onMove={(dir) => onMove(i, dir)}
        />
      ))}
      <div className="grid grid-cols-2 gap-1.5">
        <button
          type="button"
          onClick={() => {
            onAddField();
            setOpenIdx(columns.length);
          }}
          className="rounded-lg border border-dashed border-indigo-300 py-2 text-[11px] font-bold text-indigo-600 hover:bg-indigo-50"
        >
          + ستون داده
        </button>
        <button
          type="button"
          onClick={() => {
            onAddCustom();
            setOpenIdx(columns.length);
          }}
          className="rounded-lg border border-dashed border-violet-300 py-2 text-[11px] font-bold text-violet-700 hover:bg-violet-50"
        >
          + ستون سفارشی
        </button>
      </div>
    </div>
  );
}
